import { getRealDatabase } from '../../database-sqlite/DatabaseSingleton';
import { TABLE_NAME } from '../constants/tables';

/**
 * Interface para el usuario completo
 */
export interface UserProfile {
    _id: string;
    name?: string;
    last_name?: string;
    email?: string;
    user_name?: string;
    role?: string;
    avatar?: string;
    timeZone?: string;
    user_segment?: Record<string, any>;
    name_group?: string;
    ws_url?: string;
    delete_active?: boolean;
    lat?: number;
    lng?: number;
    url_uploads?: string;
    languages?: string[];
    lenguage?: string;
    phone?: string;
    email_verified?: boolean;
    phone_verified?: boolean;
    cognito_id?: string;
    login?: boolean;
    image_splash?: string;
    last_update_coords?: string;
    area?: string;
    nickname?: string;
    dni?: string;
}

/**
 * Convierte valores JavaScript a tipos compatibles con SQLite
 * Solo acepta: null, string, number, Uint8Array
 */
function toSqlValue(value: any, defaultValue: string | null = null): string | number | null {
    // Si es null o undefined, retornar el default
    if (value === null || value === undefined) {
        return defaultValue;
    }

    // Date → ISO string
    if (value instanceof Date) {
        return value.toISOString();
    }

    // Boolean → 0 o 1
    if (typeof value === 'boolean') {
        return value ? 1 : 0;
    }

    // Number → number
    if (typeof value === 'number') {
        return value;
    }

    // Array u Object → JSON string
    if (typeof value === 'object') {
        return JSON.stringify(value);
    }

    // Cualquier otra cosa → string
    return String(value);
}

/**
 * Inserta o actualiza un usuario en la base de datos
 * Usa INSERT OR REPLACE para upsert
 */
export async function upsertUser(user: UserProfile): Promise<void> {
    // console.log('🔄 Upserting user:', user);
    const realDb = await getRealDatabase();

    // Preparar valores asegurando tipos compatibles con SQLite
    const params = [
        user._id,
        toSqlValue(user.name, ''),
        toSqlValue(user.last_name, ''),
        toSqlValue(user.email, ''),
        toSqlValue(user.user_name, ''),
        toSqlValue(user.role, ''),
        toSqlValue(user.avatar, ''),
        toSqlValue(user.timeZone, ''),
        toSqlValue(user.user_segment, '{}'),
        toSqlValue(user.name_group, ''),
        toSqlValue(user.ws_url, ''),
        toSqlValue(user.delete_active, null),
        user.lat ?? 0,
        user.lng ?? 0,
        toSqlValue(user.url_uploads, ''),
        toSqlValue(user.languages, '[]'),
        toSqlValue(user.lenguage, ''),
        toSqlValue(user.phone, ''),
        user.email_verified ? 1 : 0,
        user.phone_verified ? 1 : 0,
        toSqlValue(user.cognito_id, ''),
        user.login ? 1 : 0,
        toSqlValue(user.image_splash, ''),
        toSqlValue(user.last_update_coords, ''),
        toSqlValue(user.area, ''),
        toSqlValue(user.nickname, ''),
        toSqlValue(user.dni, ''),
    ];

    await realDb.runAsync(
        `
            INSERT OR REPLACE INTO ${TABLE_NAME.USER_PROFILE} (
                _id, name, last_name, email, user_name, role, avatar, timeZone, user_segment,
                name_group, ws_url, delete_active, lat, lng, url_uploads, languages, lenguage,
                phone, email_verified, phone_verified, cognito_id, login, image_splash,
                last_update_coords, area, nickname, dni
            ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)
        `,
        params,
    );
}

/**
 * Obtiene el primer usuario de la base de datos
 * (asumiendo que solo hay un usuario activo)
 */
export async function getUser(): Promise<UserProfile | null> {
    const realDb = await getRealDatabase();

    const row = await realDb.getFirstAsync<any>(
        `SELECT * FROM ${TABLE_NAME.USER_PROFILE} LIMIT 1`
    );

    if (!row) return null;

    // Parsear campos JSON
    return {
        ...row,
        user_segment: row.user_segment ? JSON.parse(row.user_segment) : {},
        languages: row.languages ? JSON.parse(row.languages) : [],
        delete_active: Boolean(row.delete_active),
        email_verified: Boolean(row.email_verified),
        phone_verified: Boolean(row.phone_verified),
        login: Boolean(row.login),
    };
}

/**
 * Obtiene un usuario por ID
 */
export async function getUserById(userId: string): Promise<UserProfile | null> {
    const realDb = await getRealDatabase();

    const row = await realDb.getFirstAsync<any>(
        `SELECT * FROM ${TABLE_NAME.USER_PROFILE} WHERE _id = ?`,
        [userId]
    );

    if (!row) return null;

    // Parsear campos JSON
    return {
        ...row,
        user_segment: row.user_segment ? JSON.parse(row.user_segment) : {},
        languages: row.languages ? JSON.parse(row.languages) : [],
        delete_active: Boolean(row.delete_active),
        email_verified: Boolean(row.email_verified),
        phone_verified: Boolean(row.phone_verified),
        login: Boolean(row.login),
    };
}

/**
 * Elimina todos los usuarios
 */
export async function deleteAllUsers(): Promise<void> {
    const realDb = await getRealDatabase();
    await realDb.runAsync(
        `DELETE FROM ${TABLE_NAME.USER_PROFILE}`
    );
}

/**
 * Actualiza campos específicos del usuario
 */
export async function updateUserFields(
    userId: string,
    fields: Partial<Omit<UserProfile, '_id'>>
): Promise<void> {
    const realDb = await getRealDatabase();

    const updates: string[] = [];
    const params: any[] = [];

    Object.entries(fields).forEach(([key, value]) => {
        updates.push(`${key} = ?`);
        params.push(toSqlValue(value));
    });

    if (updates.length === 0) return;

    params.push(userId);

    await realDb.runAsync(
        `UPDATE ${TABLE_NAME.USER_PROFILE} SET ${updates.join(', ')} WHERE _id = ?`,
        params
    );
}

/**
 * Obtiene el ID del country asociado al segmento del usuario
 */
export async function getCountryId(): Promise<string | null> {
    try {
        const userProfile = await getUser()

        if (userProfile && userProfile.user_segment) {
            const countriesIds = userProfile.user_segment['segment_countries']
            const countryId = Array.isArray(countriesIds) && countriesIds.length > 0 ? countriesIds[0] : null
            return countryId || null
        }

        return null
    } catch (error) {
        console.error('Error getting country ID:', error)
        return null
    }
}
